import * as trace from 'owa-trace';
import CalendarEventKeyRange from '../store/schema/CalendarEventKeyRange';
import CalendarFetchOrder from '../store/schema/CalendarFetchOrder';
import datapoints from '../datapoints';
import DateRange from '../store/schema/DateRange';
import getCalendarEventsFromCache from '../store/apis/getCalendarEventsFromCache';
import { CalendarEvent } from 'mini-calendar-event-types';
import { createAppContext } from 'mini-app-context';
import { getDateRangeStore } from '../store/store';
import { traceAndReject } from 'error-handling';
import {
    addDays,
    OwaDate,
    endOfDay,
    startOfDay,
    getTimestamp,
    getDateString,
    userDate,
} from 'owa-datetime';
import computeKeyRangeAndDateRange, {
    KeyRangeOrDateRange,
} from '../utils/computeKeyRangeAndDateRange';
import reloadCalendarEventsInCache, {
    getActiveReloadRequestForDateRange,
} from '../store/apis/reloadCalendarEventsInCache';
import {
    getDateRangeLength,
    containsDateRange,
    DateRange as DisplayDateRange,
} from 'mini-datetime-utils/lib/DateRange';
import {
    convertDateRangeToDisplayDateRange,
    convertDisplayDateRangeToDateRange,
} from '../utils/epochTimeToDisplayDate';

const gceDatapoints = datapoints.DAL.GCE;

const MAX_DURATION_TO_CACHE = 110;

export interface GetCalendarEventsForRangeResponse {
    events?: CalendarEvent[];
    dateRange?: DateRange;
}

/**
 * Returns the calendar events for a given range.
 * @param requestedRange Requested range, can be either a date range or key range. If neither minEventsCount not maxEventsCount is sent, all events in the range will be returned.
 * @param order Should the events be returned from the front or end of specified range
 * @param minEventsCount Minimum number of events to return. If number of events in requested range < minEventsCount, we just return the avialable events
 * @param maxEventsCount Maximum number of events to return.
 */
export default async function getCalendarEventsForRange(
    requestedRange: KeyRangeOrDateRange,
    order: CalendarFetchOrder = CalendarFetchOrder.Ascending,
    minEventsCount?: number,
    maxEventsCount?: number,
    appContext: any = createAppContext()
): Promise<GetCalendarEventsForRangeResponse> {
    validateParams(requestedRange, minEventsCount, maxEventsCount);

    const {
        keyRange: requestedKeyRange,
        dateRange: requestedDateRange,
    } = computeKeyRangeAndDateRange(requestedRange);

    const requestedDisplayDateRange: DisplayDateRange = convertDateRangeToDisplayDateRange(
        requestedDateRange
    );
    const dateRangeLength = getDateRangeLength(requestedDisplayDateRange);
    if (dateRangeLength > MAX_DURATION_TO_CACHE) {
        const errorMessage = `Shouldn't request events for more than ${MAX_DURATION_TO_CACHE} days duration`;
        trace.errorThatWillCauseAlert(errorMessage);
        throw new Error(errorMessage);
    }

    appContext &&
        appContext.signal(gceDatapoints.REQUEST_PARAMS, {
            order: order,
            start: getDateString(requestedDisplayDateRange.start),
            end: getDateString(requestedDisplayDateRange.end),
        });

    const cacheDateRange: DateRange = getDateRangeStore().getDateRangeAlreadyFetched();
    let keyRangeToFetchFromCache = requestedKeyRange;
    let eventsFromCache = [];
    let cacheContainsRequestedDateRange = false;
    if (cacheDateRange) {
        const cachedDisplayDateRange = convertDateRangeToDisplayDateRange(cacheDateRange);
        if (shouldFetchEventsFromCache(cacheDateRange, requestedDateRange, order)) {
            // Truncated the keyrange to fetch from BTree using dateRange supported in cache. Because there can be
            // some expanded events in BTree beyond the cached dateRange but that data is not complete
            keyRangeToFetchFromCache = truncateKeyRangeToFetch(cacheDateRange, requestedKeyRange);
            eventsFromCache = getCalendarEventsFromCache(
                keyRangeToFetchFromCache,
                order,
                maxEventsCount
            );
        }
        cacheContainsRequestedDateRange = containsDateRange(
            cachedDisplayDateRange,
            requestedDisplayDateRange,
            true /* inclusive check */
        );
    }

    appContext &&
        appContext.signal(gceDatapoints.CACHE_HIT, {
            isCacheHit: cacheContainsRequestedDateRange,
            cacheStartDate: cacheDateRange && new Date(cacheDateRange.start).toDateString(),
            cacheEndDate: cacheDateRange && new Date(cacheDateRange.end).toDateString(),
            requestedStartDate: getDateString(requestedDisplayDateRange.start),
            requestedEndDate: getDateString(requestedDisplayDateRange.end),
            count: eventsFromCache.length,
        });

    const shouldFetchEventsFromServer =
        (maxEventsCount ? eventsFromCache.length < maxEventsCount : true) &&
        !cacheContainsRequestedDateRange;

    if (shouldFetchEventsFromServer) {
        let reloadPromise = null;

        const activeReloadRequestInfo = getActiveReloadRequestForDateRange(
            null,
            requestedDateRange
        );

        if (activeReloadRequestInfo) {
            // Wait for the promise if there is an active network request for the requested date range
            getDateRangeStore().setDateRange(activeReloadRequestInfo.info.dateRange);
            reloadPromise = activeReloadRequestInfo.info.reloadPromise;
            const reloadPromiseDateRange = activeReloadRequestInfo.info.dateRange;
            appContext &&
                appContext.signal(gceDatapoints.ACTIVE_RELOAD_REQUEST, {
                    activeReqInProgress: true,
                    start: new Date(reloadPromiseDateRange.start).toDateString(),
                    end: new Date(reloadPromiseDateRange.end).toDateString(),
                });
        } else {
            appContext &&
                appContext.signal(gceDatapoints.ACTIVE_RELOAD_REQUEST, {
                    activeReqInProgress: false,
                });
            // Calculate new date range to request from the server
            let offsetLength: number = (MAX_DURATION_TO_CACHE - dateRangeLength) / 2;
            offsetLength = offsetLength > 0 ? offsetLength : 0;
            const newDateRange: DisplayDateRange = {
                start: startOfDay(addDays(requestedDisplayDateRange.start, -offsetLength)),
                end: endOfDay(addDays(requestedDisplayDateRange.end, offsetLength)),
            };

            reloadPromise = reloadCalendarEventsInCache(
                convertDisplayDateRangeToDateRange(newDateRange),
                undefined, // folder Id
                appContext
            );
        }

        // If request doesn't contain minEventsCount or if events count is less than minEventsCount
        // then wait for the promise to resolve and then return the events from cache
        if (!minEventsCount || eventsFromCache.length < minEventsCount) {
            try {
                await reloadPromise;
                keyRangeToFetchFromCache = requestedKeyRange;
                eventsFromCache = getCalendarEventsFromCache(
                    keyRangeToFetchFromCache,
                    order,
                    maxEventsCount
                );
            } catch (error) {
                if (error === 'REJECT_AS_STALE') {
                    return Promise.reject(error);
                }
                return traceAndReject('getCalendarEventsForRange', error);
            }
        }
    }

    return {
        events: eventsFromCache,
        dateRange: getDateRangeFromFetchedEvents(
            keyRangeToFetchFromCache,
            eventsFromCache,
            order,
            maxEventsCount,
            appContext
        ),
    };
}

/**
 * Determines whether to fetch events for Cache or not
 * @param cacheDateRange The date range of the events cache
 * @param requestedDateRange The requested date range
 * @param order The fetch order for the requested range
 */
function shouldFetchEventsFromCache(
    cacheDateRange: DateRange,
    requestedDateRange: DateRange,
    order: CalendarFetchOrder
) {
    if (order === CalendarFetchOrder.Ascending) {
        // If fetch order is ascending, requested range start must be in cached date range
        return (
            cacheDateRange.start <= requestedDateRange.start &&
            requestedDateRange.start <= cacheDateRange.end
        );
    } else {
        // If fetch order is descending, requested range end must be in cached date range
        return (
            cacheDateRange.start <= requestedDateRange.end &&
            requestedDateRange.end <= cacheDateRange.end
        );
    }
}

/**
 * Truncates the key range to query the BTree based on cached date range
 * @param cacheDateRange The date range of the events cache
 * @param requestedKeyRange The requested key range
 */
function truncateKeyRangeToFetch(
    cacheDateRange: DateRange,
    requestedKeyRange: CalendarEventKeyRange
): CalendarEventKeyRange {
    const { start: cachedStart, end: cachedEnd } = cacheDateRange;
    const {
        start: { timestamp: requestedStartTime, itemId: requestedStartItemId },
        end: { timestamp: requestedEndTime, itemId: requestedEndItemId },
        includeEnd,
    } = requestedKeyRange;

    if (cacheDateRange) {
        return {
            start: {
                timestamp: cachedStart >= requestedStartTime ? cachedStart : requestedStartTime,
                itemId: requestedStartItemId,
            },
            end: {
                timestamp: cachedEnd >= requestedEndTime ? requestedEndTime : cachedEnd,
                itemId: requestedEndItemId,
            },
            includeEnd,
        } as CalendarEventKeyRange;
    } else {
        return requestedKeyRange;
    }
}

/**
 * Generates distinct days range based on first event and last event start time.
 * @param requestedKeyRange Requested range, can be either a date range or key range
 * @param events List of calendar events
 * @param order Should the events be returned from the front or end of specified range
 * @param maxEventsCount maximum number of events to return
 *
 */
function getDateRangeFromFetchedEvents(
    keyRange: CalendarEventKeyRange,
    events: CalendarEvent[],
    order: CalendarFetchOrder,
    maxEventsCount: number,
    appContext?: any
): DateRange {
    if (!keyRange && !events) {
        return { start: null, end: null };
    }

    const {
        start: { timestamp: startKeyTimeStamp },
        end: { timestamp: endKeyTimeStamp },
    } = keyRange;
    let startDate: OwaDate = null;
    let endDate: OwaDate = null;

    if (!maxEventsCount || events.length < maxEventsCount) {
        startDate = startOfDay(userDate(startKeyTimeStamp));
        endDate = startOfDay(userDate(endKeyTimeStamp));
    } else if (events.length === maxEventsCount && order !== undefined) {
        if (order === CalendarFetchOrder.Descending) {
            // When scrolling up
            // 1. start date should be calculated based on the first event
            // 2. end date should be the endKey timestamp to ensure we don't miss days where there are no events
            startDate = startOfDay(events[0].Start);
            endDate = startOfDay(userDate(endKeyTimeStamp));
        } else {
            // When scrolling down
            // 1. start date should be startKey timestamp to ensure we don't miss days where there are no events
            // 2. end date should be the calculated based on the last event
            startDate = startOfDay(userDate(startKeyTimeStamp));
            endDate = startOfDay(events[events.length - 1].Start);
        }
    }

    return { start: getTimestamp(startDate), end: getTimestamp(endDate) };
}

/**
 * Checks the validity of the params passed to getCalendarEventsForRange
 */
function validateParams(
    requestedRange: KeyRangeOrDateRange,
    minEventsCount: number,
    maxEventsCount: number
) {
    if (
        !requestedRange ||
        (minEventsCount && minEventsCount < 0) ||
        (maxEventsCount && maxEventsCount < 0) ||
        (minEventsCount && maxEventsCount && minEventsCount > maxEventsCount)
    ) {
        const errorMessage = 'Invalid parameters passed';
        trace.errorThatWillCauseAlert(errorMessage);
        throw new Error(errorMessage);
    }
}
